'use client'
import { Editor, Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { ReactRenderer } from '@tiptap/react'
import Suggestion, { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import tippy from 'tippy.js'

import AIMenuList from './AIMenuList.js'
// import { AICommandPanel } from "../../features/panels/AICommandPanel/AICommandPanel.js";

const extensionName = 'aiCommand'

let popup: any
let popupSelection: { from: number; to: number }

export const AICommand = Extension.create({
  name: extensionName,

  priority: 200,

  onCreate() {},

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: ' ',
        allowSpaces: false,
        startOfLine: true,
        pluginKey: new PluginKey(extensionName),
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const isRootDepth = $from.depth === 1
          const isParagraph = $from.parent.type.name === 'paragraph'
          const isStartOfNode = $from.parent.textContent?.charAt(0) === ' '
          const textLength = $from.parent.textContent?.length

          // TODO
          const isInColumn = this.editor.isActive('column')

          const afterContent = $from.parent.textContent?.substring(
            $from.parent.textContent?.indexOf(' '),
          )
          const isValidAfterContent = !afterContent?.endsWith('  ')
          return (
            ((isRootDepth && isParagraph && isStartOfNode) ||
              (isInColumn && isParagraph && isStartOfNode)) &&
            isValidAfterContent &&
            textLength === 1
          )
        },
        command: ({ editor, props }: { editor: Editor; props: any }) => {
          // const { view, state } = editor;
          // const { $head, $from } = view.state.selection;

          // const end = $from.pos;
          // const from = $head?.nodeBefore
          //   ? end -
          //     ($head.nodeBefore.text?.substring(
          //       $head.nodeBefore.text?.indexOf(" ")
          //     ).length ?? 0)
          //   : $from.start();

          // const tr = state.tr.deleteRange(from, end);
          // view.dispatch(tr);

          props.action(editor)
          // view.focus();
        },
        items: async ({ query }: { query: string }) => {
          const GROUPS = (await import('./groups.js')).default
          const withFilteredCommands = GROUPS.map((group) => ({
            ...group,
            commands: group.commands
              .filter((item) => {
                const labelNormalized = item.label.toLowerCase().trim()
                const queryNormalized = query.toLowerCase().trim()

                if (item.aliases) {
                  const aliases = item.aliases.map((alias) => alias.toLowerCase().trim())

                  return (
                    labelNormalized.includes(queryNormalized) || aliases.includes(queryNormalized)
                  )
                }

                return labelNormalized.includes(queryNormalized)
              })
              .filter((command) =>
                command.shouldBeHidden ? !command.shouldBeHidden(this.editor) : true,
              ),
          }))

          const withoutEmptyGroups = withFilteredCommands.filter((group) => {
            if (group.commands.length > 0) {
              return true
            }

            return false
          })

          const withEnabledSettings = withoutEmptyGroups.map((group) => ({
            ...group,
            commands: group.commands.map((command) => ({
              ...command,
              isEnabled: true,
            })),
          }))

          return withEnabledSettings
        },
        render: () => {
          let component: any

          let scrollHandler: (() => void) | null = null

          return {
            onStart: (props: SuggestionProps) => {
              const editor = props.editor

              const t = tippy as any
              popup = t('body', {
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                theme: 'slash-command',
                // maxWidth: "16rem",
                offset: [0, -16],
                popperOptions: {
                  strategy: 'fixed',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                  ],
                },
                editor: editor,
                onHide(instance) {
                  try {
                    const editorRef = instance.props.editor
                    const completion = editorRef?.storage?.aiCommand?.completion ?? []
                    const selection = editorRef?.state?.selection

                    if (!completion || completion === '') {
                      return
                    }
                    editorRef
                      ?.chain()
                      .focus()
                      .insertContentAt(
                        { from: popupSelection.from - 1, to: popupSelection.to },
                        ' ',
                      )
                      .insertContentAt(popupSelection.to, completion)
                      .run()

                    props.editor.storage.aiCommand.completion = ''
                  } catch (e) {
                    console.log('Error', e)
                  }
                },
              })
              component = new ReactRenderer(AIMenuList, {
                props,
                editor: props.editor,
              })
              props.editor.storage.aiCommand.active = true
              const { view } = props.editor

              const editorNode = view.dom as HTMLElement

              const getReferenceClientRect = () => {
                if (!props.clientRect) {
                  return props.editor.storage[extensionName].rect
                }

                const rect = props.clientRect()

                if (!rect) {
                  return props.editor.storage[extensionName].rect
                }

                let yPos = rect.y

                if (rect.top + component.element.offsetHeight + 40 > window.innerHeight) {
                  const diff = rect.top + component.element.offsetHeight - window.innerHeight + 40
                  yPos = rect.y - diff
                }

                // Account for when the editor is bound inside a container that doesn't go all the way to the edge of the screen
                const editorXOffset = editorNode.getBoundingClientRect().x
                const boundigClient = editorNode.getBoundingClientRect()

                return new DOMRect(rect.x, yPos, boundigClient.width, rect.height)
              }

              scrollHandler = () => {
                popup?.[0].setProps({
                  getReferenceClientRect,
                })
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              popup?.[0].setProps({
                getReferenceClientRect,
                appendTo: () => document.body,
                content: component?.element,
              })

              const selection = this?.editor?.state?.selection
              popupSelection = { from: selection?.from, to: selection?.to }

              popup?.[0].show()
            },

            onUpdate: (props: SuggestionProps) => {
              component?.updateProps(props)

              const { view } = props.editor

              const editorNode = view.dom as HTMLElement

              const getReferenceClientRect = () => {
                if (!props.clientRect) {
                  return props.editor.storage[extensionName].rect
                }

                const rect = props.clientRect()

                if (!rect) {
                  return props.editor.storage[extensionName].rect
                }

                // Account for when the editor is bound inside a container that doesn't go all the way to the edge of the screen
                return new DOMRect(rect.x, rect.y, rect.width, rect.height)
              }

              let scrollHandler = () => {
                popup?.[0].setProps({
                  getReferenceClientRect,
                })
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              // eslint-disable-next-line no-param-reassign
              props.editor.storage[extensionName].rect = props.clientRect
                ? getReferenceClientRect()
                : {
                    width: 0,
                    height: 0,
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                  }
              popup?.[0].setProps({
                getReferenceClientRect,
              })
            },

            onKeyDown: (props: SuggestionKeyDownProps) => {
              if (props.event.key === 'Escape') {
                popup?.[0].hide()

                return true
              }

              if (!popup?.[0].state.isShown) {
                popup?.[0].show()
              }

              return component?.ref?.onKeyDown(props)
            },

            onExit: (props) => {
              if (scrollHandler) {
                const { view } = props.editor
                view.dom.parentElement?.removeEventListener('scroll', scrollHandler)
              }

              props.editor.storage.aiCommand.active = false
              props.editor.storage.aiCommand.userPrompt = ''
              props.editor.storage.aiCommand.completion = ''

              component?.destroy()
            },
          }
        },
      }),
    ]
  },

  addStorage() {
    return {
      rect: {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
      },
      userPrompt: '',
      completion: '',
      title: '',
      active: false,
    }
  },
})

export default AICommand
